/**
 * DropboxPage.jsx · v2.7.52+E28 · Storage Portal Phase 2 · Production-ready
 * ═════════════════════════════════════════════════════════════════════════
 * Master-owner / super-root gated Dropbox - Business admin workspace.
 *
 * Final-execution-directive scope (no stubs, no TODO comments):
 *   • Multi-select checkboxes per row + select-all in the grid header
 *   • Adaptive contextual command bar:
 *       no items selected → Upload File · Create Folder · Refresh
 *       items selected    → Share · Download · Move · Delete · Clear
 *   • Drag-and-drop upload zone with per-file progress bars (chunked
 *     for >150 MB via the backend's chunked endpoint)
 *   • Row hover actions: Quick Share · Star toggle · 3-dot menu
 *     (Rename · Move · Download · Delete)
 *   • Pagination via Dropbox cursor (has_more + Load more button)
 *   • Activity stream log inside the detail panel
 *   • Share-link modal: public/team-only + optional password + optional
 *     expiration date
 *   • Embedded permissions matrix verification — page is master-owner /
 *     super-root only (server-enforced via /status returning 403)
 *
 * Backend contract:
 *   GET    /admin/dropbox/status                     · live ping + identity
 *   GET    /admin/dropbox/files?path=&cursor=        · paginated listing
 *   POST   /admin/dropbox/files/folder               · create folder
 *   POST   /admin/dropbox/files/upload  (multipart)  · upload single (auto-
 *                                                       chunked > 150 MB)
 *   GET    /admin/dropbox/files/download?path=       · binary stream
 *   POST   /admin/dropbox/files/delete               · single delete
 *   POST   /admin/dropbox/files/batch-delete         · multi delete
 *   POST   /admin/dropbox/files/move                 · move/rename
 *   POST   /admin/dropbox/files/share                · public/team + pwd + expires
 *   GET    /admin/dropbox/files/activity?path=       · path-scoped audit
 *   POST   /admin/dropbox/files/star                 · toggle starred
 *   GET    /admin/dropbox/files/starred              · list starred paths
 *   POST   /admin/dropbox/oauth/authorize-url        · OAuth start
 *   POST   /admin/dropbox/oauth/exchange             · OAuth refresh-token swap
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Folder, File as FileIcon, FileText, FileImage, FileVideo, FileAudio,
  FileSpreadsheet, FileArchive, Upload, FolderPlus, Share2, Download,
  Trash2, Star, MoreHorizontal, ChevronRight, Search, RefreshCw, Plug,
  AlertTriangle, Loader2, ArrowLeft, Lock, Home, Clock, CheckCircle2,
  XCircle, ExternalLink, Copy, Move, Eye, X, History, FileCheck,
} from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { DropboxGlyph, DropboxLockup, DROPBOX_BRAND_BLUE } from "@/components/brands/DropboxBrand";

// ── Helpers ────────────────────────────────────────────────────────────
const formatBytes = (n) => {
  if (n == null || isNaN(n)) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = Number(n);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};
const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const formatRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const s = Math.round((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.round(h / 24);
  if (dd < 30) return `${dd}d ago`;
  return d.toLocaleDateString();
};

function iconForName(name = "") {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (["png","jpg","jpeg","gif","webp","svg","heic","heif"].includes(ext)) return FileImage;
  if (["mp4","mov","webm","mkv","avi","wmv","mpg","mpeg"].includes(ext))   return FileVideo;
  if (["mp3","wav","ogg","flac","aac","aiff","m4a"].includes(ext))         return FileAudio;
  if (["csv","xls","xlsx","tsv","numbers"].includes(ext))                  return FileSpreadsheet;
  if (["zip","tar","gz","rar","7z"].includes(ext))                         return FileArchive;
  if (["txt","md","json","xml","html","js","ts","py","yaml","yml"].includes(ext)) return FileText;
  if (["pdf"].includes(ext))                                               return FileCheck;
  return FileIcon;
}

function pathSegments(path) {
  const parts = (path || "").split("/").filter(Boolean);
  const out = [{ label: "All files", path: "" }];
  let cur = "";
  for (const p of parts) {
    cur = `${cur}/${p}`;
    out.push({ label: p, path: cur });
  }
  return out;
}

function entryKey(entry) {
  return entry.id || entry.path_lower || entry.path_display || entry.name;
}

// ═════════════════════════════════════════════════════════════════════════
// Main page
// ═════════════════════════════════════════════════════════════════════════
export default function DropboxPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);   // null = loading
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [path, setPath] = useState("");
  const [entries, setEntries] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [filterChip, setFilterChip] = useState("all");
  const [lastListError, setLastListError] = useState(null);

  // multi-select set (of entry-keys)
  const [selection, setSelection] = useState(() => new Set());

  // starred paths (path_lower → true)
  const [starredSet, setStarredSet] = useState(() => new Set());

  // upload queue: { id, name, size, progress 0-100, status: 'queued'|'uploading'|'done'|'error', error? }
  const [uploads, setUploads] = useState([]);
  // drag-over state for the drop zone
  const [dragOver, setDragOver] = useState(false);

  // share modal state
  const [shareTarget, setShareTarget] = useState(null);
  // move modal state
  const [moveTargets, setMoveTargets] = useState(null);   // array of entries
  // rename modal state
  const [renameTarget, setRenameTarget] = useState(null);

  const fileInputRef = useRef(null);

  // ── Loaders ─────────────────────────────────────────────────────────
  const loadStatus = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/dropbox/status");
      setStatus(data);
    } catch (e) {
      const code = e?.response?.status;
      setStatus({ connected: false, denied: code === 403 });
    }
  }, []);

  const loadStarred = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/dropbox/files/starred");
      setStarredSet(new Set((data?.paths || []).map((p) => p.toLowerCase())));
    } catch { /* non-fatal */ }
  }, []);

  const loadFiles = useCallback(async (target = path, opts = {}) => {
    const { append = false, useCursor = null } = opts;
    setLoadingFiles(true);
    setLastListError(null);
    try {
      const params = useCursor ? { cursor: useCursor } : { path: target };
      const { data } = await adminApi.get("/admin/dropbox/files", { params });
      setEntries((prev) => append ? [...prev, ...(data?.entries || [])] : (data?.entries || []));
      setCursor(data?.cursor || null);
      setHasMore(!!data?.has_more);
      if (!append) setSelection(new Set());
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "List failed";
      const isScopeError = String(msg).includes("required scope") || String(msg).includes("missing_scope");
      setLastListError({ message: msg, isScopeError });
      toast.error(isScopeError ? "Dropbox app missing scopes — see banner below" : msg, {
        description: isScopeError
          ? "Enable files.metadata.read / write, files.content.read / write, sharing.read / write on the Dropbox app Permissions tab, regenerate the token, then reconnect."
          : undefined,
        duration: 8000,
      });
      if (!append) setEntries([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [path]);

  // initial loads
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    if (status?.connected) {
      loadFiles("");
      loadStarred();
    }
  }, [status?.connected, loadFiles, loadStarred]);

  // ── Navigation ──────────────────────────────────────────────────────
  const onEnterFolder = (entry) => {
    if (entry.type !== "folder") return;
    const next = entry.path_display || `${path}/${entry.name}`;
    setPath(next);
    setSelected(null);
    setSelection(new Set());
    loadFiles(next);
  };
  const onBreadcrumb = (target) => {
    setPath(target);
    setSelected(null);
    setSelection(new Set());
    loadFiles(target);
  };

  // ── Filters ─────────────────────────────────────────────────────────
  const filteredEntries = useMemo(() => {
    let list = entries;
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((e) => (e.name || "").toLowerCase().includes(q));
    if (filterChip === "starred") {
      list = list.filter((e) => starredSet.has((e.path_lower || "").toLowerCase()));
    }
    return list;
  }, [entries, search, filterChip, starredSet]);

  // ── Selection ───────────────────────────────────────────────────────
  const toggleSelect = (entry) => {
    const key = entryKey(entry);
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const selectAll = () => {
    if (selection.size === filteredEntries.length) {
      setSelection(new Set());
    } else {
      setSelection(new Set(filteredEntries.map(entryKey)));
    }
  };
  const clearSelection = () => setSelection(new Set());
  const selectedEntries = useMemo(
    () => filteredEntries.filter((e) => selection.has(entryKey(e))),
    [filteredEntries, selection],
  );

  // ── Star toggle ─────────────────────────────────────────────────────
  const toggleStar = useCallback(async (entry) => {
    const key = (entry.path_lower || entry.path_display || "").toLowerCase();
    const wasStarred = starredSet.has(key);
    // optimistic update
    setStarredSet((prev) => {
      const next = new Set(prev);
      if (wasStarred) next.delete(key); else next.add(key);
      return next;
    });
    try {
      await adminApi.post("/admin/dropbox/files/star", {
        path: entry.path_display || entry.path_lower,
        starred: !wasStarred,
      });
    } catch (e) {
      // rollback
      setStarredSet((prev) => {
        const next = new Set(prev);
        if (wasStarred) next.add(key); else next.delete(key);
        return next;
      });
      toast.error(e?.response?.data?.detail || "Could not toggle star");
    }
  }, [starredSet]);

  // ── Upload (drag-drop + click) ──────────────────────────────────────
  const beginUpload = useCallback(async (file) => {
    const id = `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 8)}`;
    setUploads((prev) => [...prev, { id, name: file.name, size: file.size, progress: 0, status: "uploading" }]);
    const form = new FormData();
    form.append("path", path || "");
    form.append("file", file);
    try {
      await adminApi.post("/admin/dropbox/files/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
          setUploads((prev) => prev.map((u) => u.id === id ? { ...u, progress: pct } : u));
        },
      });
      setUploads((prev) => prev.map((u) => u.id === id ? { ...u, progress: 100, status: "done" } : u));
      toast.success(`Uploaded "${file.name}"`);
      // remove from queue after a moment so the user sees the 100% bar
      setTimeout(() => setUploads((prev) => prev.filter((u) => u.id !== id)), 1500);
    } catch (e) {
      setUploads((prev) => prev.map((u) => u.id === id
        ? { ...u, status: "error", error: e?.response?.data?.detail || "Upload failed" }
        : u));
      toast.error(`"${file.name}" — ${e?.response?.data?.detail || "upload failed"}`);
    }
  }, [path]);

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    // run uploads in parallel for snappy UX (Dropbox handles concurrency fine)
    await Promise.allSettled(files.map(beginUpload));
    loadFiles(path);
  }, [beginUpload, loadFiles, path]);

  const onPickFiles = (e) => {
    handleFiles(e.target.files);
    e.target.value = "";  // allow re-selecting the same file
  };

  // drag-and-drop handlers attached at the workspace root
  const onDragOver = (e) => {
    if (readOnly) return;            // read-tier cannot upload
    e.preventDefault(); e.stopPropagation(); setDragOver(true);
  };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const onDrop = (e) => {
    if (readOnly) return;            // read-tier cannot upload
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    handleFiles(e.dataTransfer?.files);
  };

  // ── Batch actions ───────────────────────────────────────────────────
  const onBatchDelete = useCallback(async () => {
    if (!selectedEntries.length) return;
    const names = selectedEntries.map((e) => e.name).join(", ");
    if (!window.confirm(`Delete ${selectedEntries.length} item${selectedEntries.length===1?"":"s"}?\n\n${names}\n\nThis moves them to the Dropbox Deleted Files area.`)) return;
    try {
      const { data } = await adminApi.post("/admin/dropbox/files/batch-delete", {
        paths: selectedEntries.map((e) => e.path_display || e.path_lower),
      });
      toast.success(`Deleted ${data.succeeded}/${data.total} item${data.total===1?"":"s"}`);
      clearSelection();
      loadFiles(path);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch delete failed");
    }
  }, [selectedEntries, loadFiles, path]);

  const onBatchDownload = useCallback(async () => {
    // Dropbox API has no bulk-download; we download each sequentially.
    const files = selectedEntries.filter((e) => e.type !== "folder");
    if (!files.length) {
      toast.error("Folders cannot be downloaded directly. Open and download files individually.");
      return;
    }
    const token = localStorage.getItem("rtg_admin_token") || localStorage.getItem("admin_token") || "";
    let downloaded = 0;
    for (const entry of files) {
      const url = `/api/admin/dropbox/files/download?path=${encodeURIComponent(entry.path_display || entry.path_lower)}`;
      try {
        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(await r.text());
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = entry.name;
        a.click();
        URL.revokeObjectURL(a.href);
        downloaded++;
      } catch (e) {
        toast.error(`"${entry.name}" — download failed`);
      }
    }
    toast.success(`Downloaded ${downloaded}/${files.length} file${files.length===1?"":"s"}`);
  }, [selectedEntries]);

  // ── Early-return guards ─────────────────────────────────────────────
  if (status === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-300">
        <Loader2 className="h-5 w-5 animate-spin mr-2"/>Loading Dropbox…
      </div>
    );
  }
  if (status?.denied) return <DeniedScreen onBack={() => navigate("/admin")} />;
  if (!status?.connected) {
    return <ConnectScreen onBack={() => navigate("/admin")}
                          canConnect={status?.access_level === "write"}
                          onConnected={loadStatus} />;
  }

  const hasSelection = selection.size > 0;
  // v2.7.53+E29 · 3-tier access matrix
  // status.access_level is "write" (full) or "read" (view + download + star only)
  const canWrite = (status?.access_level === "write");
  const readOnly = !canWrite;

  return (
    <div
      className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col relative"
      data-testid="dropbox-page"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Drag-and-drop overlay */}
      {dragOver && (
        <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center"
             style={{ background: "rgba(0, 97, 255, 0.15)", border: "4px dashed #0061FF" }}
             data-testid="dropbox-dropzone-overlay">
          <div className="bg-zinc-900 border border-sky-400/60 rounded-xl px-10 py-8 text-center shadow-2xl">
            <Upload className="h-10 w-10 mx-auto mb-3 text-sky-400"/>
            <div className="text-xl font-extrabold text-zinc-100">Drop to upload</div>
            <div className="text-sm text-zinc-400 mt-1">Files will be saved to <code className="text-sky-300">{path || "/"}</code></div>
          </div>
        </div>
      )}

      {/* ── Top brand strip ─────────────────────────────────────────── */}
      <header className="border-b border-zinc-800 bg-zinc-900/40 px-4 sm:px-6 py-3 flex items-center gap-4 flex-shrink-0">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}
                className="text-zinc-400 hover:text-zinc-100 -ml-2"
                data-testid="dropbox-back">
          <ArrowLeft className="h-4 w-4 mr-1"/>Back
        </Button>
        <DropboxLockup height={28} />
        <div className="text-zinc-500 mx-2">·</div>
        <span className="text-sm uppercase tracking-widest text-zinc-400 font-bold">Business</span>
        <div className="flex-1"/>
        <div className="relative w-full max-w-xl">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"/>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search current folder…"
            className="pl-9 bg-zinc-900 border-zinc-700 text-zinc-100"
            data-testid="dropbox-search"
          />
        </div>
        <div className="flex-1"/>
        <ConnectionPill status={status} onRefresh={loadStatus} />
      </header>

      {/* ── 3-column layout ─────────────────────────────────────────── */}
      <div className="flex-1 flex min-h-0">
        {/* Left subnav */}
        <aside className="w-60 flex-shrink-0 border-r border-zinc-800 bg-zinc-900/30 p-3 hidden md:flex flex-col gap-1 overflow-y-auto" data-testid="dropbox-left-nav">
          <SubNavItem icon={Home} label="All files" active={path === "" && filterChip === "all"} onClick={() => { setFilterChip("all"); onBreadcrumb(""); }} testid="dropbox-nav-all"/>
          <SubNavItem icon={Clock} label="Recents" disabled />
          <SubNavItem icon={Star} label={`Starred (${starredSet.size})`} active={filterChip === "starred"} onClick={() => setFilterChip("starred")} testid="dropbox-nav-starred"/>
          <SubNavItem icon={Trash2} label="Deleted files" disabled />
          <div className="border-t border-zinc-800 my-3"/>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold px-2 mb-1">Connected</div>
          <div className="px-2 text-xs text-zinc-400 truncate" title={status?.account_email}>
            {status?.account_name || "—"}
          </div>
          <div className="px-2 text-[10px] text-zinc-500 truncate" title={status?.account_email}>
            {status?.account_email}
          </div>
          {status?.team_name && (
            <div className="px-2 mt-1 text-[10px] uppercase tracking-widest font-bold text-sky-400">
              {status.team_name}
            </div>
          )}
          {status?.has_access_token_only && canWrite && (
            <UpgradeOAuthCard onUpgraded={loadStatus} />
          )}
        </aside>

        {/* Center column */}
        <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* Page header */}
          <div className="px-4 sm:px-6 pt-5 pb-3 border-b border-zinc-800 flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <Breadcrumbs segments={pathSegments(path)} onClick={onBreadcrumb} />
              <h1 className="text-xl sm:text-2xl font-extrabold text-zinc-100 mt-1">
                {filterChip === "starred" ? "Starred" : (pathSegments(path).slice(-1)[0]?.label || "All files")}
              </h1>
            </div>
            {/* Adaptive contextual command bar */}
            {hasSelection ? (
              <div className="flex items-center gap-2 flex-wrap" data-testid="dropbox-cmd-selection">
                <span className="text-xs text-sky-300 font-bold px-2 py-1 bg-sky-500/10 border border-sky-400/40 rounded">
                  {selection.size} selected
                </span>
                {canWrite && selectedEntries.length === 1 && (
                  <ToolbarButton icon={Share2} label="Share" onClick={() => setShareTarget(selectedEntries[0])} testid="dropbox-cmd-share"/>
                )}
                <ToolbarButton icon={Download} label="Download" onClick={onBatchDownload} testid="dropbox-cmd-download"/>
                {canWrite && (
                  <>
                    <ToolbarButton icon={Move} label="Move" onClick={() => setMoveTargets(selectedEntries)} testid="dropbox-cmd-move"/>
                    <ToolbarButton icon={Trash2} label="Delete" onClick={onBatchDelete} testid="dropbox-cmd-delete" danger/>
                  </>
                )}
                <ToolbarButton icon={X} label="Clear" onClick={clearSelection} testid="dropbox-cmd-clear"/>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap" data-testid="dropbox-cmd-default">
                {canWrite && (
                  <>
                    <ToolbarButton icon={Upload} label="Upload file" onClick={() => fileInputRef.current?.click()} testid="dropbox-upload-btn"/>
                    <ToolbarButton icon={FolderPlus} label="Create folder" onClick={() => promptCreateFolder(path, () => loadFiles(path))} testid="dropbox-folder-btn"/>
                  </>
                )}
                <ToolbarButton icon={RefreshCw} label="Refresh" onClick={() => loadFiles(path)} testid="dropbox-refresh-btn"/>
                {readOnly && <ReadOnlyBadge />}
                {canWrite && (
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={onPickFiles}
                    data-testid="dropbox-file-input"
                  />
                )}
              </div>
            )}
          </div>

          {/* Scope-error banner (only on real scope errors) */}
          {lastListError?.isScopeError && (
            <ScopeHintBanner accountEmail={status?.account_email} />
          )}

          {/* Upload queue */}
          {uploads.length > 0 && (
            <UploadQueue queue={uploads} onClear={(id) => setUploads((p) => p.filter((u) => u.id !== id))} />
          )}

          {/* Filter chips */}
          <div className="px-4 sm:px-6 py-3 flex items-center gap-2 border-b border-zinc-800/50">
            <Chip active={filterChip === "all"} onClick={() => setFilterChip("all")} icon={null} label="All"/>
            <Chip active={filterChip === "starred"} onClick={() => setFilterChip("starred")} icon={Star} label={`Starred${starredSet.size ? ` (${starredSet.size})` : ""}`}/>
            <div className="flex-1"/>
            {filteredEntries.length > 0 && (
              <label className="text-xs text-zinc-400 flex items-center gap-1.5 cursor-pointer hover:text-zinc-200 select-none">
                <input
                  type="checkbox"
                  checked={selection.size > 0 && selection.size === filteredEntries.length}
                  onChange={selectAll}
                  className="accent-sky-500"
                  data-testid="dropbox-select-all"
                />
                Select all
              </label>
            )}
            <span className="text-xs text-zinc-500">
              {filteredEntries.length} item{filteredEntries.length === 1 ? "" : "s"}
            </span>
          </div>

          {/* File grid */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {loadingFiles && entries.length === 0 ? (
              <div className="text-center text-zinc-500 py-12">
                <Loader2 className="h-5 w-5 animate-spin inline mr-2"/>Loading…
              </div>
            ) : filteredEntries.length === 0 ? (
              <EmptyState path={path} filterChip={filterChip}/>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="dropbox-grid">
                  {filteredEntries.map((entry) => (
                    <FileGridTile
                      key={entryKey(entry)}
                      entry={entry}
                      canWrite={canWrite}
                      active={selected && entryKey(selected) === entryKey(entry)}
                      selected={selection.has(entryKey(entry))}
                      starred={starredSet.has((entry.path_lower || "").toLowerCase())}
                      onSelect={() => setSelected(entry)}
                      onToggleCheck={() => toggleSelect(entry)}
                      onEnter={() => onEnterFolder(entry)}
                      onShare={() => setShareTarget(entry)}
                      onStar={() => toggleStar(entry)}
                      onRename={() => setRenameTarget(entry)}
                      onMove={() => setMoveTargets([entry])}
                      onDelete={async () => {
                        if (!window.confirm(`Delete "${entry.name}"?`)) return;
                        try {
                          await adminApi.post("/admin/dropbox/files/delete", { path: entry.path_display || entry.path_lower });
                          toast.success(`Deleted "${entry.name}"`);
                          loadFiles(path);
                          if (selected && entryKey(selected) === entryKey(entry)) setSelected(null);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Delete failed");
                        }
                      }}
                    />
                  ))}
                </div>

                {hasMore && (
                  <div className="text-center py-6">
                    <Button
                      onClick={() => loadFiles(path, { append: true, useCursor: cursor })}
                      disabled={loadingFiles}
                      variant="outline"
                      className="bg-zinc-900 border-zinc-700 text-zinc-100 hover:bg-zinc-800"
                      data-testid="dropbox-load-more"
                    >
                      {loadingFiles ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : null}
                      Load more
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>

        {/* Right detail panel */}
        {selected ? (
          <DetailPanel
            entry={selected}
            canWrite={canWrite}
            starred={starredSet.has((selected.path_lower || "").toLowerCase())}
            onClose={() => setSelected(null)}
            onRefresh={() => loadFiles(path)}
            onShare={() => setShareTarget(selected)}
            onStar={() => toggleStar(selected)}
            onMove={() => setMoveTargets([selected])}
            onRename={() => setRenameTarget(selected)}
          />
        ) : (
          <aside className="hidden xl:flex w-72 flex-shrink-0 border-l border-zinc-800 bg-zinc-900/20 items-center justify-center text-zinc-600 text-xs italic px-4 text-center">
            Select a file or folder to see details and quick actions here.
          </aside>
        )}
      </div>

      {/* Modals */}
      {shareTarget && (
        <ShareLinkDialog
          entry={shareTarget}
          onClose={() => setShareTarget(null)}
        />
      )}
      {moveTargets && (
        <MoveDialog
          entries={moveTargets}
          currentPath={path}
          onClose={() => setMoveTargets(null)}
          onDone={() => { setMoveTargets(null); loadFiles(path); clearSelection(); }}
        />
      )}
      {renameTarget && (
        <RenameDialog
          entry={renameTarget}
          onClose={() => setRenameTarget(null)}
          onDone={() => { setRenameTarget(null); loadFiles(path); }}
        />
      )}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// Sub-components
// ═════════════════════════════════════════════════════════════════════════
function SubNavItem({ icon: Icon, label, active = false, disabled = false, onClick, testid }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded px-2.5 py-1.5 text-sm flex items-center gap-2 transition-colors
        ${active ? "bg-sky-500/10 text-sky-300 font-bold" : "text-zinc-300 hover:bg-zinc-800/60"}
        ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      <Icon className="h-4 w-4"/>{label}
    </button>
  );
}

function ToolbarButton({ icon: Icon, label, onClick, testid, danger = false }) {
  return (
    <Button
      variant="outline" size="sm" onClick={onClick}
      data-testid={testid}
      className={`gap-1.5 ${danger
        ? "bg-rose-500/10 border-rose-500/30 text-rose-300 hover:bg-rose-500/20"
        : "bg-zinc-900 border-zinc-700 text-zinc-100 hover:bg-zinc-800"}`}
    >
      <Icon className="h-3.5 w-3.5"/>
      <span className="hidden sm:inline">{label}</span>
    </Button>
  );
}

function Breadcrumbs({ segments, onClick }) {
  return (
    <div className="flex items-center gap-1 text-[11px] text-zinc-500" data-testid="dropbox-breadcrumbs">
      {segments.map((s, i) => (
        <span key={s.path || "root"} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3 w-3 text-zinc-700"/>}
          <button
            type="button"
            onClick={() => onClick(s.path)}
            className={`hover:text-zinc-200 transition-colors ${i === segments.length - 1 ? "text-zinc-200 font-bold" : ""}`}
          >
            {s.label}
          </button>
        </span>
      ))}
    </div>
  );
}

function Chip({ active, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1
        ${active ? "bg-sky-500/15 border-sky-400/40 text-sky-200" : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"}`}
    >
      {Icon && <Icon className="h-3 w-3"/>}{label}
    </button>
  );
}

function ConnectionPill({ status, onRefresh }) {
  const ok = !!status?.live;
  return (
    <button
      type="button"
      onClick={onRefresh}
      data-testid="dropbox-connection-pill"
      className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border flex items-center gap-1.5
        ${ok ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300" : "border-rose-400/30 bg-rose-500/10 text-rose-300"}`}
    >
      {ok ? <CheckCircle2 className="h-3 w-3"/> : <XCircle className="h-3 w-3"/>}
      {ok ? "Connected" : "Auth error"}
    </button>
  );
}

function ScopeHintBanner() {
  return (
    <div className="mx-4 sm:mx-6 mt-4 rounded border border-amber-400/30 bg-amber-500/10 p-3 flex items-start gap-3 text-amber-200 text-xs" data-testid="dropbox-scope-banner">
      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5"/>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-amber-100 mb-0.5">Missing Dropbox scopes</div>
        <div className="leading-relaxed">
          Open{" "}
          <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-100">
            Dropbox App Console
          </a>
          {" "}→ Permissions tab → enable <b>files.metadata.read / write</b>, <b>files.content.read / write</b>, <b>sharing.read / write</b> → Submit → regenerate token → reconnect.
        </div>
      </div>
    </div>
  );
}

function UpgradeOAuthCard({ onUpgraded }) {
  const [step, setStep] = useState("idle");
  const [code, setCode] = useState("");

  const start = async () => {
    setStep("authorizing");
    try {
      const { data } = await adminApi.post("/admin/dropbox/oauth/authorize-url", {});
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build authorize URL");
      setStep("idle");
    }
  };
  const exchange = async () => {
    if (!code.trim()) { toast.error("Paste the code from Dropbox first"); return; }
    setStep("exchanging");
    try {
      await adminApi.post("/admin/dropbox/oauth/exchange", { code: code.trim() });
      toast.success("Refresh token saved — permanent connection complete.");
      setStep("done");
      onUpgraded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Code exchange failed");
      setStep("authorizing");
    }
  };

  return (
    <div className="mt-3 mx-2 rounded border border-amber-400/30 bg-amber-500/5 p-3 text-xs" data-testid="dropbox-oauth-upgrade">
      <div className="font-bold text-amber-200 flex items-center gap-1.5 mb-1.5">
        <Plug className="h-3.5 w-3.5"/>Upgrade to permanent connection
      </div>
      <div className="text-amber-100/80 mb-2 leading-relaxed">
        You&apos;re connected with a 4-hour access token. Get a long-lived refresh token below.
      </div>
      {step === "idle" && (
        <Button size="sm" onClick={start} data-testid="dropbox-oauth-start"
                className="bg-amber-500 hover:bg-amber-400 text-zinc-950 w-full font-bold">
          Open Dropbox authorize page
        </Button>
      )}
      {(step === "authorizing" || step === "exchanging") && (
        <div className="space-y-2">
          <div className="text-amber-100/70 text-[11px] leading-relaxed">
            1. Click &quot;Allow&quot; on the Dropbox tab.<br/>
            2. Copy the code Dropbox shows.<br/>
            3. Paste it here and click Confirm.
          </div>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Paste authorization code here"
            className="bg-zinc-950 border-amber-400/30 text-zinc-100 text-[11px]"
            data-testid="dropbox-oauth-code"
          />
          <Button size="sm" onClick={exchange} disabled={step === "exchanging"}
                  data-testid="dropbox-oauth-exchange"
                  className="bg-amber-500 hover:bg-amber-400 text-zinc-950 w-full font-bold">
            {step === "exchanging" ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "Confirm code"}
          </Button>
        </div>
      )}
      {step === "done" && (
        <div className="text-emerald-300 flex items-center gap-1.5 font-bold">
          <CheckCircle2 className="h-3.5 w-3.5"/>Permanent connection active.
        </div>
      )}
    </div>
  );
}

function EmptyState({ path, filterChip }) {
  if (filterChip === "starred") {
    return (
      <div className="text-center py-16 text-zinc-500" data-testid="dropbox-empty">
        <Star className="h-12 w-12 mx-auto mb-3 opacity-40"/>
        <div className="text-sm italic">No starred items yet.</div>
        <div className="text-xs mt-1 text-zinc-600">Click the star on any row to keep it close at hand.</div>
      </div>
    );
  }
  return (
    <div className="text-center py-16 text-zinc-500" data-testid="dropbox-empty">
      <Folder className="h-12 w-12 mx-auto mb-3 opacity-40"/>
      <div className="text-sm italic">This folder is empty.</div>
      <div className="text-xs mt-1 text-zinc-600">Drag files anywhere on this page to upload to <code>{path || "/"}</code>.</div>
    </div>
  );
}

function UploadQueue({ queue, onClear }) {
  return (
    <div className="mx-4 sm:mx-6 mt-3 rounded border border-sky-400/30 bg-sky-500/5 p-3 space-y-2" data-testid="dropbox-upload-queue">
      <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold flex items-center gap-1.5">
        <Upload className="h-3 w-3"/>Uploads · {queue.length} in queue
      </div>
      {queue.map((u) => (
        <div key={u.id} className="flex items-center gap-3 text-xs" data-testid={`dropbox-upload-row-${u.name}`}>
          <FileIcon className="h-3.5 w-3.5 text-zinc-400 flex-shrink-0"/>
          <span className="truncate flex-1 text-zinc-200">{u.name}</span>
          <span className="text-zinc-500 flex-shrink-0">{formatBytes(u.size)}</span>
          <div className="w-32 h-1.5 bg-zinc-800 rounded overflow-hidden flex-shrink-0">
            <div
              className={`h-full transition-all ${u.status === "error" ? "bg-rose-400" : u.status === "done" ? "bg-emerald-400" : "bg-sky-400"}`}
              style={{ width: `${u.progress}%` }}
            />
          </div>
          <span className="w-9 text-right text-zinc-400 flex-shrink-0 tabular-nums">
            {u.status === "error" ? "ERR" : u.status === "done" ? "✓" : `${u.progress}%`}
          </span>
          {(u.status === "error" || u.status === "done") && (
            <button type="button" onClick={() => onClear(u.id)} className="text-zinc-600 hover:text-zinc-300">
              <X className="h-3 w-3"/>
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function FileGridTile({ entry, canWrite, active, selected, starred, onSelect, onToggleCheck, onEnter,
                       onShare, onStar, onRename, onMove, onDelete }) {
  const isFolder = entry.type === "folder";
  const Icon = isFolder ? Folder : iconForName(entry.name);
  const [menuOpen, setMenuOpen] = useState(false);

  // close 3-dot menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menuOpen]);

  return (
    <Card
      data-testid={`dropbox-tile-${entry.name}`}
      onClick={onSelect}
      onDoubleClick={isFolder ? onEnter : undefined}
      className={`group cursor-pointer aspect-square bg-zinc-900 border-zinc-800 hover:border-sky-400/40 p-3 flex flex-col items-center justify-center text-center transition-all relative
        ${active ? "ring-2 ring-sky-400/60 border-sky-400/60" : ""}
        ${selected ? "ring-2 ring-sky-500 bg-sky-500/5" : ""}`}
    >
      {/* Selection checkbox (top-left) */}
      <div
        className={`absolute top-2 left-2 transition-opacity ${selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleCheck}
          className="h-4 w-4 accent-sky-500 cursor-pointer"
          data-testid={`dropbox-check-${entry.name}`}
        />
      </div>

      {/* Hover actions (top-right). Star is always available (per-user
          bookmark). Share + 3-dot mutations only render for write tier. */}
      <div
        className="absolute top-1.5 right-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-zinc-900/95 backdrop-blur rounded border border-zinc-800 px-1 py-0.5"
        onClick={(e) => e.stopPropagation()}
      >
        {canWrite && (
          <RowAction icon={Share2} title="Share"
                     onClick={onShare}
                     testid={`dropbox-row-share-${entry.name}`}/>
        )}
        <RowAction
          icon={Star}
          title={starred ? "Unstar" : "Star"}
          onClick={onStar}
          testid={`dropbox-row-star-${entry.name}`}
          highlighted={starred}
        />
        <div className="relative">
          <RowAction icon={MoreHorizontal} title="More"
                     onClick={() => setMenuOpen((v) => !v)}
                     testid={`dropbox-row-more-${entry.name}`}/>
          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-1 z-10 w-40 bg-zinc-950 border border-zinc-700 rounded shadow-xl py-1 text-xs text-zinc-200"
              onClick={(e) => e.stopPropagation()}
              data-testid={`dropbox-row-menu-${entry.name}`}
            >
              <MenuItem icon={Eye} label="Preview" onClick={() => { setMenuOpen(false); onSelect(); }}/>
              {isFolder && <MenuItem icon={Folder} label="Open" onClick={() => { setMenuOpen(false); onEnter(); }}/>}
              {canWrite && <MenuItem icon={Share2} label="Share" onClick={() => { setMenuOpen(false); onShare(); }}/>}
              {canWrite && <MenuItem icon={Move} label="Move" onClick={() => { setMenuOpen(false); onMove(); }}/>}
              {canWrite && <MenuItem icon={FileText} label="Rename" onClick={() => { setMenuOpen(false); onRename(); }}/>}
              {canWrite && <div className="border-t border-zinc-800 my-1"/>}
              {canWrite && <MenuItem icon={Trash2} label="Delete" danger onClick={() => { setMenuOpen(false); onDelete(); }}/>}
            </div>
          )}
        </div>
      </div>

      {/* Star badge (bottom-right, persistent when starred) */}
      {starred && (
        <div className="absolute bottom-2 right-2 text-amber-400" title="Starred">
          <Star className="h-3.5 w-3.5 fill-amber-400"/>
        </div>
      )}

      <Icon
        className={`h-14 w-14 mb-2 ${isFolder ? "" : "text-zinc-400"}`}
        style={isFolder ? { color: DROPBOX_BRAND_BLUE } : undefined}
        fill={isFolder ? DROPBOX_BRAND_BLUE : "none"}
        strokeWidth={isFolder ? 0 : 1.5}
      />
      <div className="text-xs font-bold text-zinc-100 truncate w-full" title={entry.name}>{entry.name}</div>
      <div className="text-[10px] text-zinc-500 mt-0.5">
        {isFolder ? "Folder" : formatBytes(entry.size)}
        {entry.server_modified && !isFolder && (
          <span className="text-zinc-600"> · {formatRelative(entry.server_modified)}</span>
        )}
      </div>
    </Card>
  );
}

function RowAction({ icon: Icon, title, onClick, testid, highlighted = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-1 rounded hover:bg-zinc-800 transition-colors ${highlighted ? "text-amber-400" : "text-zinc-300"}`}
    >
      <Icon className={`h-3.5 w-3.5 ${highlighted ? "fill-amber-400" : ""}`}/>
    </button>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full px-3 py-1.5 text-left flex items-center gap-2 hover:bg-zinc-800 ${danger ? "text-rose-300" : "text-zinc-200"}`}
    >
      <Icon className="h-3.5 w-3.5"/>{label}
    </button>
  );
}

function DetailPanel({ entry, canWrite, starred, onClose, onRefresh, onShare, onStar, onMove, onRename }) {
  const [activity, setActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const Icon = entry.type === "folder" ? Folder : iconForName(entry.name);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingActivity(true);
      try {
        const { data } = await adminApi.get("/admin/dropbox/files/activity", {
          params: { path: entry.path_display || entry.path_lower, limit: 25 },
        });
        if (!cancelled) setActivity(Array.isArray(data) ? data : []);
      } catch { /* non-fatal */ }
      finally { if (!cancelled) setLoadingActivity(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [entry]);

  const downloadFile = () => {
    const url = `/api/admin/dropbox/files/download?path=${encodeURIComponent(entry.path_display || entry.path_lower)}`;
    const token = localStorage.getItem("rtg_admin_token") || localStorage.getItem("admin_token") || "";
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.blob() : r.text().then((t) => Promise.reject(t)))
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = entry.name;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(`Downloaded "${entry.name}"`);
      })
      .catch((err) => toast.error(`Download failed: ${err}`));
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${entry.name}"? This moves it to the Dropbox Deleted Files area.`)) return;
    try {
      await adminApi.post("/admin/dropbox/files/delete", { path: entry.path_display || entry.path_lower });
      toast.success(`Deleted "${entry.name}"`);
      onClose();
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <aside className="hidden xl:flex w-80 flex-shrink-0 border-l border-zinc-800 bg-zinc-900/20 flex-col overflow-y-auto" data-testid="dropbox-detail">
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Details</div>
        <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid="dropbox-detail-close">
          <XCircle className="h-4 w-4"/>
        </button>
      </div>
      <div className="p-5 flex flex-col items-center text-center">
        <Icon
          className="h-24 w-24 mb-3"
          style={entry.type === "folder" ? { color: DROPBOX_BRAND_BLUE } : { color: "#94a3b8" }}
          fill={entry.type === "folder" ? DROPBOX_BRAND_BLUE : "none"}
          strokeWidth={entry.type === "folder" ? 0 : 1.5}
        />
        <div className="text-base font-extrabold text-zinc-100 break-all">{entry.name}</div>
        <div className="text-xs text-zinc-500 mt-1">
          {entry.type === "folder" ? "Folder" : formatBytes(entry.size)}
        </div>
        {entry.server_modified && (
          <div className="text-[11px] text-zinc-500 mt-0.5">
            Modified {formatDate(entry.server_modified)}
          </div>
        )}
      </div>
      <div className="px-4 pb-4 space-y-2">
        {entry.type !== "folder" && (
          <Button onClick={downloadFile} className="w-full justify-start gap-2 bg-zinc-800 hover:bg-zinc-700"
                  data-testid="dropbox-detail-download">
            <Download className="h-4 w-4"/>Download
          </Button>
        )}
        {canWrite && (
          <Button onClick={onShare} className="w-full justify-start gap-2 bg-zinc-800 hover:bg-zinc-700"
                  data-testid="dropbox-detail-share">
            <Share2 className="h-4 w-4"/>Create shared link
          </Button>
        )}
        <Button onClick={onStar} variant="outline"
                className={`w-full justify-start gap-2 ${starred ? "border-amber-400/40 text-amber-300 bg-amber-500/10 hover:bg-amber-500/20" : "border-zinc-700 text-zinc-200 hover:bg-zinc-800"}`}
                data-testid="dropbox-detail-star">
          <Star className={`h-4 w-4 ${starred ? "fill-amber-400" : ""}`}/>{starred ? "Unstar" : "Star"}
        </Button>
        {canWrite && (
          <Button onClick={onMove} variant="outline" className="w-full justify-start gap-2 border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                  data-testid="dropbox-detail-move">
            <Move className="h-4 w-4"/>Move
          </Button>
        )}
        {canWrite && (
          <Button onClick={onRename} variant="outline" className="w-full justify-start gap-2 border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                  data-testid="dropbox-detail-rename">
            <FileText className="h-4 w-4"/>Rename
          </Button>
        )}
        {canWrite && (
          <Button onClick={remove} variant="outline"
                  className="w-full justify-start gap-2 border-rose-500/30 text-rose-300 hover:bg-rose-500/10"
                  data-testid="dropbox-detail-delete">
            <Trash2 className="h-4 w-4"/>Delete
          </Button>
        )}
        {!canWrite && (
          <div className="text-[10px] text-zinc-500 italic text-center py-1" data-testid="dropbox-detail-readonly">
            Read-only access · ask your administrator for write privileges to upload, share, move, or delete.
          </div>
        )}
        <a href={entry.path_display ? `https://www.dropbox.com/home${encodeURI(entry.path_display)}` : "#"}
           target="_blank" rel="noopener noreferrer"
           className="block text-center text-[11px] text-sky-400 hover:text-sky-300 mt-2"
           data-testid="dropbox-detail-open-web">
          <ExternalLink className="h-3 w-3 inline mr-1"/>Open in Dropbox web
        </a>
      </div>

      {/* Activity stream */}
      <div className="border-t border-zinc-800 px-4 py-3" data-testid="dropbox-detail-activity">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5 mb-2">
          <History className="h-3 w-3"/>Activity
        </div>
        {loadingActivity ? (
          <div className="text-xs text-zinc-500"><Loader2 className="h-3 w-3 animate-spin inline mr-1"/>Loading…</div>
        ) : activity.length === 0 ? (
          <div className="text-xs text-zinc-600 italic">No activity recorded for this item yet.</div>
        ) : (
          <ul className="space-y-2 text-xs">
            {activity.map((row, i) => (
              <li key={`${row.at}-${i}`} className="flex items-start gap-2 leading-tight">
                <span className="text-sky-400 mt-0.5">●</span>
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-200 font-bold capitalize">{(row.action || "").replace(/_/g, " ")}</div>
                  <div className="text-[10px] text-zinc-500">
                    {row.actor} · {formatRelative(row.at)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-auto p-4 border-t border-zinc-800 text-[10px] text-zinc-600">
        <div className="font-mono break-all">{entry.path_display}</div>
      </div>
    </aside>
  );
}

function DeniedScreen({ onBack }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-950 text-zinc-200 p-6" data-testid="dropbox-denied">
      <Lock className="h-12 w-12 text-rose-400 mb-3"/>
      <h1 className="text-xl font-bold mb-1">Access Denied</h1>
      <p className="text-sm text-zinc-400 mb-4 max-w-md text-center">
        Dropbox - Business is restricted to the master system owner and super-root accounts.
      </p>
      <Button variant="outline" onClick={onBack} className="gap-2">
        <ArrowLeft className="h-4 w-4"/>Back to Admin Console
      </Button>
    </div>
  );
}

function ConnectScreen({ onBack, canConnect, onConnected }) {
  const [appKey, setAppKey] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!appKey.trim() || !appSecret.trim() || !accessToken.trim()) {
      toast.error("App Key, App Secret, and Access Token are required.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/admin/dropbox/connect", {
        app_key: appKey.trim(),
        app_secret: appSecret.trim(),
        access_token: accessToken.trim(),
      });
      toast.success("Dropbox connected — refresh-token upgrade available next.");
      onConnected?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save credentials");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-950 text-zinc-100 p-6">
      <DropboxLockup height={48} />
      <Card className="mt-6 w-full max-w-lg bg-zinc-900 border-zinc-800 p-6 space-y-4" data-testid="dropbox-connect-card">
        <div>
          <h1 className="text-xl font-extrabold mb-1">Connect Dropbox - Business</h1>
          <p className="text-sm text-zinc-400">
            {canConnect
              ? "Paste your Dropbox app credentials below. You'll be able to upgrade to a permanent refresh token via OAuth right after."
              : "Dropbox is not connected yet. Only Owner, Developer, and Operating-Partner accounts can connect a new Dropbox app — ask them to set this up first."}
          </p>
        </div>
        {canConnect && (
          <>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-bold">App Key (Client ID)</label>
              <Input value={appKey} onChange={(e) => setAppKey(e.target.value)} className="bg-zinc-950 border-zinc-700 mt-1" data-testid="dropbox-connect-key"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-bold">App Secret</label>
              <Input value={appSecret} onChange={(e) => setAppSecret(e.target.value)} type="password" className="bg-zinc-950 border-zinc-700 mt-1" data-testid="dropbox-connect-secret"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Access Token (4 hours)</label>
              <Input value={accessToken} onChange={(e) => setAccessToken(e.target.value)} type="password" className="bg-zinc-950 border-zinc-700 mt-1" data-testid="dropbox-connect-token"/>
              <p className="text-[11px] text-zinc-500 mt-1">
                Generate at <a className="underline text-sky-400" href="https://www.dropbox.com/developers/apps" target="_blank" rel="noreferrer">Dropbox App Console</a> → your app → Settings → Generated access token. We&apos;ll upgrade to a permanent refresh token via OAuth right after.
              </p>
            </div>
            <Button onClick={save} disabled={busy} data-testid="dropbox-connect-save"
                    className="w-full text-zinc-950 font-bold" style={{ backgroundColor: DROPBOX_BRAND_BLUE }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <Plug className="h-4 w-4 mr-2"/>}
              Connect
            </Button>
          </>
        )}
        <Button variant="ghost" onClick={onBack} className="w-full text-zinc-400">
          <ArrowLeft className="h-4 w-4 mr-2"/>Back to Admin Console
        </Button>
      </Card>
    </div>
  );
}


function ReadOnlyBadge() {
  return (
    <span
      className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border border-amber-400/40 bg-amber-500/10 text-amber-300 flex items-center gap-1.5"
      data-testid="dropbox-readonly-badge"
      title="You have view-and-download access. Upload, share, move, and delete are reserved for Developer, Operating-Partner, and Owner roles."
    >
      <Eye className="h-3 w-3"/>Read-only access
    </span>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Modals
// ═════════════════════════════════════════════════════════════════════════
function ShareLinkDialog({ entry, onClose }) {
  const [isPublic, setIsPublic] = useState(true);
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [useExpires, setUseExpires] = useState(false);
  const [expiresDate, setExpiresDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const create = async () => {
    if (useExpires && !expiresDate) { toast.error("Pick an expiration date"); return; }
    if (usePassword && password.length < 4) { toast.error("Password must be at least 4 characters"); return; }
    setBusy(true);
    try {
      const expIso = useExpires ? new Date(expiresDate).toISOString() : null;
      const { data } = await adminApi.post("/admin/dropbox/files/share", {
        path:     entry.path_display || entry.path_lower,
        public:   isPublic,
        password: usePassword ? password : null,
        expires:  expIso,
      });
      setResult(data);
      navigator.clipboard?.writeText(data.url).catch(() => {});
      toast.success("Shared link copied to clipboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="dropbox-share-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <Share2 className="h-4 w-4 text-sky-400"/>Share &quot;{entry.name}&quot;
          </DialogTitle>
        </DialogHeader>
        {!result ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-zinc-950/50 border border-zinc-800 rounded p-3">
              <div>
                <div className="text-sm font-bold text-zinc-200">Visibility</div>
                <div className="text-xs text-zinc-500">{isPublic ? "Anyone with the link can view" : "Only your team can view"}</div>
              </div>
              <button
                type="button"
                onClick={() => setIsPublic((v) => !v)}
                className={`px-3 py-1 rounded text-xs font-bold ${isPublic ? "bg-sky-500/20 text-sky-300 border border-sky-400/40" : "bg-zinc-800 text-zinc-300 border border-zinc-700"}`}
                data-testid="dropbox-share-visibility"
              >
                {isPublic ? "Public" : "Team only"}
              </button>
            </div>

            <div className="bg-zinc-950/50 border border-zinc-800 rounded p-3 space-y-2">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="text-sm font-bold text-zinc-200">Password protect</div>
                  <div className="text-xs text-zinc-500">Recipients must enter the password to view</div>
                </div>
                <Switch checked={usePassword} onCheckedChange={setUsePassword} data-testid="dropbox-share-pw-toggle"/>
              </label>
              {usePassword && (
                <Input
                  type="text"
                  placeholder="Set a password (≥ 4 chars)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-zinc-950 border-zinc-700"
                  data-testid="dropbox-share-pw-input"
                />
              )}
            </div>

            <div className="bg-zinc-950/50 border border-zinc-800 rounded p-3 space-y-2">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="text-sm font-bold text-zinc-200">Auto-expire</div>
                  <div className="text-xs text-zinc-500">Link stops working after the chosen date</div>
                </div>
                <Switch checked={useExpires} onCheckedChange={setUseExpires} data-testid="dropbox-share-exp-toggle"/>
              </label>
              {useExpires && (
                <Input
                  type="datetime-local"
                  value={expiresDate}
                  onChange={(e) => setExpiresDate(e.target.value)}
                  className="bg-zinc-950 border-zinc-700"
                  data-testid="dropbox-share-exp-input"
                />
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className={`rounded border p-3 text-xs ${result.reused
              ? "border-amber-400/40 bg-amber-500/10 text-amber-100"
              : "border-emerald-400/40 bg-emerald-500/10 text-emerald-100"}`}>
              <div className="font-bold mb-1 flex items-center gap-1.5">
                {result.reused ? (
                  <><AlertTriangle className="h-3.5 w-3.5"/>Existing link reused (copied)</>
                ) : (
                  <><CheckCircle2 className="h-3.5 w-3.5"/>Link created (copied to clipboard)</>
                )}
              </div>
              <div className={`font-mono break-all ${result.reused ? "text-amber-200" : "text-emerald-200"}`}>
                {result.url}
              </div>
              {result.reused && (
                <div className="mt-1 text-amber-300 text-[10px] leading-relaxed">
                  A shared link already existed for this item. The settings you chose were not re-applied. To force new settings, revoke the link first via the Dropbox web UI.
                </div>
              )}
              {result.expires && (
                <div className={`mt-1 ${result.reused ? "text-amber-300" : "text-emerald-300"}`}>
                  Expires {formatDate(result.expires)}
                </div>
              )}
            </div>
            <Button
              onClick={() => navigator.clipboard?.writeText(result.url)}
              variant="outline"
              className="w-full justify-start gap-2 border-zinc-700 text-zinc-200 hover:bg-zinc-800"
              data-testid="dropbox-share-copy-again"
            >
              <Copy className="h-4 w-4"/>Copy link again
            </Button>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="text-zinc-400" data-testid="dropbox-share-close">Close</Button>
          {!result && (
            <Button onClick={create} disabled={busy} className="text-zinc-950 font-bold"
                    style={{ backgroundColor: DROPBOX_BRAND_BLUE }} data-testid="dropbox-share-create">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <Share2 className="h-4 w-4 mr-2"/>}
              Create shared link
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveDialog({ entries, currentPath, onClose, onDone }) {
  const [target, setTarget] = useState(currentPath || "");
  const [busy, setBusy] = useState(false);

  const execute = async () => {
    if (!target.startsWith("/")) { toast.error('Target path must start with "/"'); return; }
    setBusy(true);
    const tasks = entries.map((entry) => {
      const newPath = `${target.replace(/\/+$/, "")}/${entry.name}`;
      return adminApi.post("/admin/dropbox/files/move", {
        from_path: entry.path_display || entry.path_lower,
        to_path:   newPath,
      });
    });
    const results = await Promise.allSettled(tasks);
    const ok = results.filter((r) => r.status === "fulfilled").length;
    if (ok === entries.length) {
      toast.success(`Moved ${ok} item${ok === 1 ? "" : "s"}`);
    } else {
      toast.error(`Moved ${ok}/${entries.length} — some failed`);
    }
    setBusy(false);
    onDone();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="dropbox-move-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <Move className="h-4 w-4 text-sky-400"/>Move {entries.length} item{entries.length === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-zinc-950/50 border border-zinc-800 rounded p-2 text-xs text-zinc-400 max-h-32 overflow-y-auto">
            {entries.map((e) => (
              <div key={entryKey(e)} className="font-mono">{e.path_display || e.name}</div>
            ))}
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Destination folder path</label>
            <Input
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder='e.g. "/Archive/2026"'
              className="bg-zinc-950 border-zinc-700 mt-1 font-mono"
              data-testid="dropbox-move-target"
            />
            <p className="text-[11px] text-zinc-500 mt-1">
              Must start with <code>/</code>. The folder must already exist in Dropbox.
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="text-zinc-400" data-testid="dropbox-move-close">Cancel</Button>
          <Button onClick={execute} disabled={busy} className="text-zinc-950 font-bold"
                  style={{ backgroundColor: DROPBOX_BRAND_BLUE }} data-testid="dropbox-move-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <Move className="h-4 w-4 mr-2"/>}
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenameDialog({ entry, onClose, onDone }) {
  const [name, setName] = useState(entry.name);
  const [busy, setBusy] = useState(false);

  const execute = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === entry.name) { onClose(); return; }
    if (/[\/\\]/.test(trimmed)) { toast.error("Name cannot contain slashes."); return; }
    setBusy(true);
    const parent = (entry.path_display || "").split("/").slice(0, -1).join("/");
    const newPath = `${parent}/${trimmed}`;
    try {
      await adminApi.post("/admin/dropbox/files/move", {
        from_path: entry.path_display,
        to_path:   newPath,
      });
      toast.success(`Renamed to "${trimmed}"`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-sm" data-testid="dropbox-rename-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-100">Rename &quot;{entry.name}&quot;</DialogTitle>
        </DialogHeader>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          className="bg-zinc-950 border-zinc-700"
          data-testid="dropbox-rename-input"
          onKeyDown={(e) => { if (e.key === "Enter") execute(); }}
        />
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="text-zinc-400" data-testid="dropbox-rename-cancel">Cancel</Button>
          <Button onClick={execute} disabled={busy} className="text-zinc-950 font-bold"
                  style={{ backgroundColor: DROPBOX_BRAND_BLUE }} data-testid="dropbox-rename-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : null}Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── Imperative helpers (outside the React tree) ────────────────────────
async function promptCreateFolder(currentPath, onDone) {
  const name = window.prompt("Folder name");
  if (!name?.trim()) return;
  try {
    await adminApi.post("/admin/dropbox/files/folder", {
      path: `${currentPath || ""}/${name.trim()}`,
    });
    toast.success(`Created "${name.trim()}"`);
    onDone();
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Folder creation failed");
  }
}
